import { useEffect, useRef, useState } from "react";
import { useGetTalentCount } from "../hooks/useGetTalentCount";
import { HomeCard } from "./HomeCard";
import { TalentProgress } from "./TalentProgress";
import { JobSuggestions } from "./JobSuggestions";
import { useGetUserInfo } from "hooks/useGetUserInfo";
import { ColumnsType } from "antd/es/table";
import { routePath } from "config/routeMgt/routePath";
import { Link } from "react-router-dom";
import { format, parseISO, isValid } from "date-fns";
import { CalendarDays } from "lucide-react";
import { DashboardShimmer } from "components/layout/components/loaders/Shimmer";
import { formatDate } from "admin/Authentication/hooks/useDateFormat";
import { useTour } from "components/layout/components/tours/TourGuideContext";
import { loadTour } from "components/layout/components/tours/tourStorage";

interface JobType {
  id: string;
  slug: any;
  listing: any;
  created_at: any;
}
interface TalentCountProps {
  jobsSearch: string;
  setJobSearch: React.Dispatch<React.SetStateAction<string>>;
}

export const TalentCount = ({ jobsSearch, setJobSearch }: TalentCountProps) => {
  const { data, isLoading } = useGetTalentCount();
  const [statusFilter, setStatusFilter] = useState<string[]>([]);
  const { token, userInfo } = useGetUserInfo();
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [visibilityScore, setVisibilityScore] = useState(0);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [visibilityItems, setVisibilityItems] = useState<any[]>([]);
  const [showAllJobs, setShowAllJobs] = useState(false);
  const jobListRef = useRef<HTMLDivElement>(null);
  const visibility = data?.visibility_score || 0;

  const tourRef = useRef<any>(null);
  const recTourRef = useRef<any>(null);
  const { start } = useTour();


  useEffect(() => {
    if (data && userInfo) {
      setLoading(false);

      const duration = 2000;
      const startTime = Date.now();
      const startValue = 0;
      const endValue = visibility;

      const animate = () => {
        const elapsed = Date.now() - startTime;
        const progress = Math.min(elapsed / duration, 1);
        const value = Math.floor(
          startValue + progress * (endValue - startValue)
        );

        setVisibilityScore(value);

        if (progress < 1) {
          requestAnimationFrame(animate);
        }
      };

      requestAnimationFrame(animate);
    }
  }, [data, userInfo, visibility]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (data?.profile.total !== 100 && !loading) {
        setIsOpen(true);
      }
    }, 3000);
    return () => clearTimeout(timer);
  }, [data, loading]);




  useEffect(() => {
    const tour = loadTour('talent');
    if (tour.completed) return;

    start('talent');
    if (userInfo?.type === 'talent') {
      tourRef.current?.start('talent');
    } else {
      recTourRef.current?.start('talent');
    }
  }, [userInfo]);

   const displayNameMap: Record<string, string> = {
     "Online Prescence": "Online Presence",
     "Elite Instituion Experience": "Elite Institution Experience",
   };

  useEffect(() => {
    if (!userInfo?.talent || !data?.visibility_score_details) return;

    const scoreDetails = data.visibility_score_details.score_details;
    if (!Array.isArray(scoreDetails)) return;

    const newItems: any[] = [];
    const newSuggestions: string[] = [];

    scoreDetails.forEach(
      (item: { score: number | null; weight: number; comment: string; category: string }) => {
        const currentScore = item.score ?? 0;
        const maxScore = item.weight;
        const percentage =
          maxScore > 0 ? Math.round((currentScore / maxScore) * 100) : 0;

        if (item.category === "Elite Institution Experience" || item.category === "Elite Instituion Experience") {
          return;
        }

        let status: "success" | "warning" | "neutral";
        let color: string;

        if (currentScore >= maxScore) {
          status = "success";
          color = "bg-green-500";
        } else if (currentScore > maxScore * 0.5) {
          status = "warning";
          color = "bg-yellow-500";
        } else {
          status = "neutral";
          color = "bg-gray-300";
        }

        if (item.comment) {
          newSuggestions.push(item.comment);
        }

        newItems.push({
          category: item.category,
          currentScore,
          maxScore,
          percentage,
          status,
          color,
          suggestion: item.comment,
          comment: item.comment,
        });
      }
    );

    setVisibilityItems(newItems);
    setSuggestions(newSuggestions);
  }, [data, userInfo]);

  const filteredJobs: JobType[] = (
    data?.liked_jobs ?? []
  ).filter((job: any) =>
    job.listing?.title
      ?.toLowerCase()
      .includes(jobsSearch.toLowerCase()) &&
    (statusFilter.length === 0 ||
      statusFilter.includes(job.listing.status))
  );


  const jobsColumns: ColumnsType<JobType> = [
    {
      title: "Job Title",
      dataIndex: "title",
      key: "title",
      sorter: (a, b) => a.listing.title.localeCompare(b.listing.title),
      render: (_, val) => (
        <div>
          <Link
            to={routePath.jobDetails(val.slug).path}
            className="font-medium text-primary block"
          >
            {val.listing.title}
          </Link>
        </div>
      ),
    },
    {
      title: "Date Posted",
      dataIndex: "created_at",
      key: "created_at",
      sorter: (a, b) =>
        new Date(a.listing.created_at).getTime() -
        new Date(b.listing.created_at).getTime(),
      render: (_, val) => formatDate(val.listing.created_at)
    },
    {
      title: "Date Applied",
      dataIndex: "created_at",
      key: "created_at",
      sorter: (a, b) =>
        new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
      render: (_, val) => formatDate(val.created_at)
    },
  ];

  const formatDate = (date: string | Date) => {
    const d = new Date(date);

    if (isNaN(d.getTime())) return "--/---/----";

    return d.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const safeFormatDate = (date?: string) => {
    if (!date) return "--/---/----";

    const parsedDate = parseISO(date);

    if (!isValid(parsedDate)) return "--/---/----";

    return format(parsedDate, "dd/MMM/yyyy");
  };


  return (
    <>
      {/* <TalentTourGuide ref={tourRef} />
      <RecruiterTourGuide ref={recTourRef} /> */}

      <div className="w-full bg-white rounded-2xl p-8 shadow-sm border border-gray-200">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
          <div className="flex flex-col items-center justify-center text-center">
            <div className="relative w-56 h-56 mb-6">
              <svg className="w-full h-full" viewBox="0 0 100 100">
                <circle
                  cx="50"
                  cy="50"
                  r="45"
                  fill="none"
                  stroke="#EEF2FF"
                  strokeWidth="8"
                />

                <circle
                  cx="50"
                  cy="50"
                  r="45"
                  fill="none"
                  stroke="#01065A"
                  strokeWidth="8"
                  strokeLinecap="round"
                  strokeDasharray="283"
                  strokeDashoffset={283 - (283 * visibilityScore) / 100}
                  transform="rotate(-90 50 50)"
                />

                <text
                  x="50"
                  y="50"
                  textAnchor="middle"
                  dy=".3em"
                  fontSize="28"
                  fontWeight="600"
                  fill="#000"
                >
                  {visibilityScore}%
                </text>
              </svg>
            </div>

            <h2 className="text-xl font-semibold text-gray-900">
              Your Profile Strength
            </h2>

            <p className="text-gray-600 text-sm mt-2 max-w-xs">
              This score shows how visible your profile is to recruiters. Higher
              scores increase your chances of being discovered.
            </p>
          </div>

          <div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              Boost Your Profile by Category
            </h2>

            <p className="text-sm text-gray-500 mb-6 leading-relaxed max-w-lg">
              Grey indicates that no information has been provided yet. Amber
              means some information has been entered, but the section is still
              incomplete. Green means the information is fully provided and the
              section is complete.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {visibilityItems.map((item, idx) => (
                <div
                  key={idx}
                  className="flex items-center justify-between bg-white rounded-xl border border-gray-200 px-4 py-3"
                >
                  <span className="text-sm text-gray-700">
                    {displayNameMap[item.category] || item.category}{" "}
                  </span>
                  <span
                    className={`
        w-4 h-4 rounded-full 
        ${item.status === "success" ? "bg-green-400" : "bg-gray-300"}
      `}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        <HomeCard
          title="Accepted"
          count={data?.accepted_invites ? data?.accepted_invites : 0}
          percentage={40}
        />
        <HomeCard
          title="Pending"
          count={data?.pending_invites ? data?.pending_invites : 0}
          percentage={10}
        />
        <HomeCard
          title="Rejected"
          count={data?.rejected_invites ? data?.rejected_invites : 0}
          percentage={20}
        />

        {!loading && data?.profile.total !== 100 && (
          <TalentProgress
            open={isOpen}
            data={data?.profile}
            handleClose={() => setIsOpen(false)}
          />
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mt-10">
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200 h-full">
          <h3 className="text-xl font-semibold mb-6">Jobs Applied</h3>

          <div className="relative mb-6">
            <input
              type="text"
              placeholder="Search Jobs"
              value={jobsSearch}
              onChange={(e) => setJobSearch(e.target.value)}
              className="w-full border border-gray-200 rounded-lg py-3 pl-10 pr-4 text-gray-700 focus:outline-none focus:ring-1 focus:ring-gray-300"
            />
            <span className="absolute left-3 top-3.5 text-gray-400">🔍</span>
          </div>

          {/* Job List */}
          <div className="space-y-6">
            <div
              ref={jobListRef}
              className={`space-y-6 ${
                showAllJobs ? "max-h-[400px] overflow-y-auto pr-2" : ""
              }`}
            >
              {filteredJobs
                .slice(0, showAllJobs ? filteredJobs.length : 2)
                .map((job: JobType) => {
                  const datePosted = safeFormatDate(job?.listing?.created_at);
                  const dateApplied = safeFormatDate(job?.created_at);

                  return (
                    <Link
                      key={job.slug}
                      to={`/jobs/${job.listing.slug}`}
                      className="block pb-4 border-b border-gray-200 hover:bg-gray-50 cursor-pointer"
                    >
                      <h4 className="text-base font-semibold text-gray-900">
                        {job.listing.title}
                      </h4>

                      <p className="text-base text-gray-900">
                        Company Name:{" "}
                        <span className="font-semibold">
                          {job.listing.company_name}
                        </span>
                      </p>

                      <div className="mt-2 space-y-1">
                        <div className="flex items-center gap-2 text-gray-600 text-sm">
                          <CalendarDays className="w-4 h-4" />
                          <span>Date Posted: {formatDate(datePosted)}</span>
                        </div>

                        <div className="flex items-center gap-2 text-gray-600 text-sm">
                          <CalendarDays className="w-4 h-4" />
                          <span>Date Applied: {formatDate(dateApplied)}</span>
                        </div>
                      </div>
                    </Link>
                  );
                })}
            </div>

            {filteredJobs.length > 3 && (
              <button
                onClick={() => setShowAllJobs(!showAllJobs)}
                className="text-blue-700 font-semibold underline text-sm hover:text-blue-800 transition-colors"
              >
                {showAllJobs ? "See less..." : "See more..."}
              </button>
            )}
          </div>
        </div>

        {data?.job_suggestions && data.job_suggestions.length > 0 && (
          <div className="mt-8">
            <JobSuggestions jobSuggestions={data.job_suggestions} />
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4 mt-12">
        <div className="bg-white rounded-lg shadow-md h-full p-6">
          <div className="pb-6">
            <div>
              <h3 className="text-base font-bold">Profile Metrics</h3>
            </div>
          </div>
          <div className="relative">
            <div>
              <ul className="p-2 mt-2 space-y-2 w-full">
                <li>
                  <div className="flex w-full py-1 px-3 text-sm font-semibold rounded">
                    <span className="self-center rounded-full w-[42px] h-[42px] bg-gray-100 relative text-center p-2">
                      <i className="ri-check-fill text-xl text-indigo-500"></i>
                    </span>
                    <div className="ml-6 pl-2 pt-1 text-base text-start">
                      <div className="mb-1 text-gray-800">Profile Views</div>
                      <div className="text-gray-500 text-xs hidden sm:block">
                        Number of times the profile has been viewed by
                        recruiters
                      </div>
                    </div>
                    <div className="ml-auto pl-2 pt-1 text-base text-end">
                      <div className="font-bold text-green-500">
                        {data?.profile_view_count}
                      </div>
                    </div>
                  </div>
                </li>

                <li>
                  <div className="flex w-full py-1 px-3 text-sm font-semibold rounded">
                    <span className="self-center rounded-full bg-gray-100 relative text-center p-2">
                      <i className="ri-chat-1-line text-xl text-indigo-500"></i>
                    </span>
                    <div className="ml-6 pl-2 pt-1 text-base text-start">
                      <div className="mb-1 text-gray-800">
                        Pre-chats Scheduled
                      </div>
                      <div className="text-gray-500 text-xs hidden sm:block">
                        Number of pre-chats invited and attended
                      </div>
                    </div>
                    <div className="ml-auto pl-2 pt-1 text-base text-end">
                      <div className="font-bold text-green-500">
                        {data?.accepted_invites}
                      </div>
                    </div>
                  </div>
                </li>
                <li>
                  <div className="flex w-full py-1 px-3 text-sm font-semibold rounded">
                    <span className="self-center rounded-full bg-gray-100 relative text-center p-2">
                      <i className="ri-briefcase-4-line text-xl text-indigo-500"></i>
                    </span>
                    <div className="ml-6 pl-2 pt-1 text-base text-start">
                      <div className="mb-1 text-gray-800">Offers Received</div>
                      <div className="text-gray-500 text-xs hidden sm:block">
                        Number of job offers received
                      </div>
                    </div>
                    <div className="ml-auto pl-2 pt-1 text-base text-end">
                      <div className="font-bold text-green-500">
                        {data?.number_of_job_offers}
                      </div>
                    </div>
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md h-full p-6">
          <div className="pb-6">
            <div>
              <h3 className="text-base font-bold">Activity Metrics</h3>
            </div>
          </div>
          <div className="relative">
            <div>
              <ul className="p-2 mt-2 space-y-2 w-full">
                <li>
                  <div className="flex w-full py-1 px-3 text-sm font-semibold rounded">
                    <span className="self-center rounded-full w-[42px] h-[42px] bg-gray-100 relative text-center p-2">
                      <i className="ri-message-3-fill text-xl text-indigo-500"></i>
                    </span>
                    <div className="ml-6 pl-2 pt-1 text-base text-start">
                      <div className="mb-1 text-gray-800">
                        Message Response Rate
                      </div>
                      <div className="text-gray-500 text-xs hidden sm:block">
                        Percentage of messages from recruiters responded to
                      </div>
                    </div>
                    <div className="ml-auto pl-2 pt-1 text-base text-end">
                      <div className="font-bold text-green-500">
                        {data?.message_response_rate}
                      </div>
                    </div>
                  </div>
                </li>
                <li>
                  <div className="flex w-full py-1 px-3 text-sm font-semibold rounded">
                    <span className="self-center rounded-full bg-gray-100 relative text-center p-2">
                      <i className="ri-time-line text-xl text-indigo-500"></i>
                    </span>
                    <div className="ml-6 pl-2 pt-1 text-base text-start">
                      <div className="mb-1 text-gray-800">
                        Talent's Response Time
                      </div>
                      <div className="text-gray-500 text-xs hidden sm:block">
                        Average time taken to respond to recruiter messages
                      </div>
                    </div>
                    <div className="ml-auto pl-2 pt-1 text-base text-end">
                      <div className="font-bold text-green-500">
                        {data?.message_response_time}
                      </div>
                    </div>
                  </div>
                </li>

                <li>
                  <div className="flex w-full py-1 px-3 text-sm font-semibold rounded">
                    <span className="self-center rounded-full bg-gray-100 relative text-center p-2">
                      <i className="ri-search-2-line text-xl text-indigo-500"></i>
                    </span>
                    <div className="ml-6 pl-2 pt-1 text-base text-start">
                      <div className="mb-1 text-gray-800">
                        Search Visibility
                      </div>
                      <div className="text-gray-500 text-xs hidden sm:block">
                        How often the talent appears in recruiter searches.
                      </div>
                    </div>
                    <div className="ml-auto pl-2 pt-1 text-base text-end">
                      <div className="font-bold text-green-500">
                        {data?.search_visibility}
                      </div>
                    </div>
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </>
  )
};
